import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import { authService } from '../services/authService';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [tokenStatus, setTokenStatus] = useState('validating'); // validating, valid, invalid
  const [maskedEmail, setMaskedEmail] = useState('');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errors, setErrors] = useState({});

  // Handle page loader fadeout
  useEffect(() => {
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$('.loader1').fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenStatus('invalid');
      return;
    }

    const validateToken = async () => {
      try {
        const result = await authService.validateResetToken(token);
        setMaskedEmail(result.email);
        setTokenStatus('valid');
      } catch (err) {
        setTokenStatus('invalid');
      }
    };

    validateToken();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Password validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setStatus('loading');
    setErrors({});

    try {
      await authService.resetPassword(token, formData.password, formData.confirmPassword);
      setStatus('success');
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setStatus('error');
      if (err.data) {
        // Handle validation errors from backend
        if (typeof err.data === 'object') {
          setErrors(err.data);
        } else {
          setErrors({ form: err.message || 'Failed to reset password. Please try again.' });
        }
      } else {
        setErrors({ form: err.message || 'Failed to reset password. Please try again.' });
      }
    }
  };


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[ResetPassword] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={-100}
          className="paralax-section-slide-data1"
        />
      )}

      {/* Main Content */}
      <section className="login wide-tb-100">
        <div className="container">
          <div className="row">
            <div className="offset-lg-3 col-lg-6 col-md-12 col-sm-12 whitebox">
              <div className="widget logincontainer">

                {/* Validating Token */}
                {tokenStatus === 'validating' && (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-3">Validating reset link...</p>
                  </div>
                )}

                {/* Invalid Token */}
                {tokenStatus === 'invalid' && (
                  <div className="text-center">
                    <div className="alert alert-danger" role="alert">
                      <h4 className="alert-heading">Invalid or Expired Link</h4>
                      <p>
                        This password reset link is invalid or has expired.
                      </p>
                      <hr />
                      <p className="mb-0">
                        Password reset links are only valid for 1 hour.
                      </p>
                    </div>
                    <Link to="/forgot-password" className="btn btn-default mt-3">
                      Request New Link
                    </Link>
                  </div>
                )}

                {/* Success Message */}
                {status === 'success' && (
                  <div className="text-center">
                    <div className="alert alert-success" role="alert">
                      <h4 className="alert-heading">Password Reset Successful!</h4>
                      <p>
                        Your password has been reset successfully.
                      </p>
                      <hr />
                      <p className="mb-0">
                        Redirecting to login page...
                      </p>
                    </div>
                    <Link to="/login" className="btn btn-default mt-3">
                      Go to Login
                    </Link>
                  </div>
                )}

                {/* Reset Form */}
                {tokenStatus === 'valid' && status !== 'success' && (
                  <>
                    <h3 className="bottom35 text-center">Reset Password</h3>
                    {maskedEmail && (
                      <p className="text-center text-muted mb-4">
                        Resetting password for: <strong>{maskedEmail}</strong>
                      </p>
                    )}

                    {errors.form && (
                      <div className="alert alert-danger" role="alert">
                        {errors.form}
                      </div>
                    )}

                    <form className="getin_form border-form" onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-md-12 col-sm-12">
                          <div className="form-group">
                            <input
                              type="password"
                              name="password"
                              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                              placeholder="New Password"
                              value={formData.password}
                              onChange={handleChange}
                              required
                              disabled={status === 'loading'}
                            />
                            {errors.password && (
                              <div className="invalid-feedback">{errors.password}</div>
                            )}
                            <small className="form-text text-muted">
                              Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special character
                            </small>
                          </div>
                        </div>

                        <div className="col-md-12 col-sm-12">
                          <div className="form-group">
                            <input
                              type="password"
                              name="confirmPassword"
                              className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                              placeholder="Confirm Password"
                              value={formData.confirmPassword}
                              onChange={handleChange}
                              required
                              disabled={status === 'loading'}
                            />
                            {errors.confirmPassword && (
                              <div className="invalid-feedback">{errors.confirmPassword}</div>
                            )}
                          </div>
                        </div>

                        <div className="col-sm-12">
                          <button
                            type="submit"
                            className="form-btn btn-theme bg-navy-blue"
                            disabled={status === 'loading'}
                          >
                            {status === 'loading' ? 'Resetting...' : 'Reset Password'}
                          </button>
                        </div>
                      </div>
                    </form>

                    <p className="text-center top20">
                      Remember your password? <Link to="/login">Sign In</Link>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default ResetPassword;
